import React from 'react';
import { DollarSign, TrendingUp, Calculator } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { MailingCostKPIs } from '../../services/ppcFarmCostService';

interface MailingCostKPICardsProps {
  kpis: MailingCostKPIs;
  isLoading?: boolean;
  className?: string;
}

const MailingCostKPICards: React.FC<MailingCostKPICardsProps> = ({
  kpis,
  isLoading = false,
  className
}) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ${className || ''}`}>
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="flex items-center space-x-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const metricsData = [
    {
      label: "Total Direct Mail Spend",
      value: formatCurrency(kpis.totalDirectMailSpend),
      icon: DollarSign,
      color: "text-yellow-600 dark:text-yellow-400",
      bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
    },
    {
      label: "Total Data Scrape Spend",
      value: formatCurrency(kpis.totalDataScrapeSpend),
      icon: TrendingUp,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      label: "Total Other Costs",
      value: formatCurrency(kpis.totalOtherCosts),
      icon: Calculator,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
    },
    {
      label: "Grand Total Spend",
      value: formatCurrency(kpis.grandTotalSpend),
      icon: Calculator,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
  ];

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ${className || ''}`}>
      {metricsData.map((metric, index) => {
        const IconComponent = metric.icon;
        return (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-3 flex items-center justify-center">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <IconComponent className={`h-4 w-4 ${metric.color} mr-2`} />
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {metric.label}
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {metric.value}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default MailingCostKPICards;